import { Copy, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './raid-finder.css';

type RaidCandidate = {
  userId: string;
  userLogin: string;
  userName: string;
  gameId: string;
  gameName: string;
  title: string;
  viewerCount: number;
  startedAt: string | null;
  language: string | null;
  thumbnailUrl: string | null;
  mature: boolean;
};

type RaidSnapshot = {
  connected: boolean;
  live: boolean;
  broadcasterId: string | null;
  current: {
    gameId: string;
    gameName: string;
    viewerCount: number;
  } | null;
  candidates: RaidCandidate[];
  fetchedAt: string;
  lastError: string | null;
};

async function loadRaidFinder(): Promise<RaidSnapshot> {
  const response = await fetch('/api/v1/integrations/twitch/raid-finder', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(payload.message ?? `Raid finder request failed (${response.status}).`);
  }
  return response.json() as Promise<RaidSnapshot>;
}

function previewUrl(value: string | null): string | null {
  if (!value) return null;
  return value.replace('{width}', '640').replace('{height}', '360');
}

function formatStarted(value: string | null): string {
  if (!value) return 'Start time unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Start time unavailable';
  return `Live since ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export function RaidFinderPage() {
  const [snapshot, setSnapshot] = useState<RaidSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [sort, setSort] = useState<'closest' | 'high' | 'low' | 'name'>('closest');

  const refresh = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      setSnapshot(await loadRaidFinder());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Raid finder could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en-GB');
    const min = minimum.trim() ? Number(minimum) : null;
    const max = maximum.trim() ? Number(maximum) : null;
    const currentCount = snapshot?.current?.viewerCount ?? 0;

    return [...(snapshot?.candidates ?? [])]
      .filter((candidate) => {
        if (Number.isFinite(min) && min !== null && candidate.viewerCount < min) return false;
        if (Number.isFinite(max) && max !== null && candidate.viewerCount > max) return false;
        if (!query) return true;
        return `${candidate.userName} ${candidate.userLogin} ${candidate.title} ${candidate.gameName}`
          .toLocaleLowerCase('en-GB')
          .includes(query);
      })
      .sort((left, right) => {
        if (sort === 'high') return right.viewerCount - left.viewerCount || left.userName.localeCompare(right.userName);
        if (sort === 'low') return left.viewerCount - right.viewerCount || left.userName.localeCompare(right.userName);
        if (sort === 'name') return left.userName.localeCompare(right.userName, 'en-GB', { sensitivity: 'base' });
        return Math.abs(left.viewerCount - currentCount) - Math.abs(right.viewerCount - currentCount)
          || right.viewerCount - left.viewerCount
          || left.userName.localeCompare(right.userName, 'en-GB', { sensitivity: 'base' });
      });
  }, [maximum, minimum, search, snapshot, sort]);

  async function copyRaid(login: string) {
    const command = `/raid ${login}`;
    try {
      await navigator.clipboard.writeText(command);
      setMessage(`Copied ${command}. Paste it into Twitch chat when you are ready.`);
    } catch {
      window.prompt('Copy raid command', command);
    }
  }

  function openChannel(login: string) {
    window.open(`https://www.twitch.tv/${encodeURIComponent(login)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <section className="ffs-workspace-page ffs-raid-page">
      <section className="ffs-workspace-hero">
        <div>
          <small>TWITCH DISCOVERY</small>
          <h2>Native Raid Finder</h2>
          <p>Find live channels in your current Twitch category. FFSControl never starts the raid itself.</p>
        </div>
        <div className="ffs-raid-hero-actions">
          <span className={`ffs-status ${snapshot?.live ? 'good' : 'warning'}`}>
            {snapshot?.live ? 'STREAM LIVE' : 'NOT LIVE'}
          </span>
          <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      {snapshot?.lastError ? <div className="ffs-workspace-message">{snapshot.lastError}</div> : null}

      <section className="ffs-workspace-card ffs-raid-current">
        <header>
          <div><small>CURRENT STREAM</small><h3>{snapshot?.current?.gameName || 'No live category'}</h3></div>
          <strong>{snapshot?.current ? `${snapshot.current.viewerCount.toLocaleString('en-GB')} viewers` : 'Raid finder waits for a live stream'}</strong>
        </header>
        <p>
          {snapshot?.live
            ? `${(snapshot.candidates ?? []).length.toLocaleString('en-GB')} same-category live channels returned by Twitch.`
            : 'Go live before using the same-category finder. No outbound action is performed from this page.'}
        </p>
      </section>

      <section className="ffs-workspace-card ffs-raid-results">
        <header>
          <div><small>CANDIDATES</small><h3>Choose where to go next</h3></div>
          <strong>{visible.length.toLocaleString('en-GB')} shown</strong>
        </header>

        <div className="ffs-raid-toolbar">
          <label className="ffs-search-field"><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search channel or title" /></label>
          <label><span>Min viewers</span><input inputMode="numeric" value={minimum} onChange={(event) => setMinimum(event.target.value.replace(/\D/g, '').slice(0, 7))} placeholder="Any" /></label>
          <label><span>Max viewers</span><input inputMode="numeric" value={maximum} onChange={(event) => setMaximum(event.target.value.replace(/\D/g, '').slice(0, 7))} placeholder="Any" /></label>
          <label><span>Sort</span><select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}><option value="closest">Closest to my viewers</option><option value="high">Most viewers</option><option value="low">Fewest viewers</option><option value="name">Channel name</option></select></label>
        </div>

        {!loading && snapshot?.live && visible.length === 0 ? <div className="ffs-empty-state">No live raid candidates match the current filters.</div> : null}

        <div className="ffs-raid-grid">
          {visible.map((candidate) => {
            const image = previewUrl(candidate.thumbnailUrl);
            return (
              <article className="ffs-raid-card" key={candidate.userId}>
                <div className="ffs-raid-preview">
                  {image ? <img src={image} alt="" loading="lazy" /> : <div className="placeholder">No preview</div>}
                  <span>{candidate.viewerCount.toLocaleString('en-GB')} viewers</span>
                </div>
                <div className="ffs-raid-card-body">
                  <div className="ffs-raid-name"><strong>{candidate.userName}</strong><span>@{candidate.userLogin}</span></div>
                  <p>{candidate.title || 'Untitled stream'}</p>
                  <small>{candidate.language ? candidate.language.toUpperCase() : '—'} · {formatStarted(candidate.startedAt)}{candidate.mature ? ' · Mature' : ''}</small>
                  <div className="ffs-raid-actions">
                    <button type="button" className="ffs-button secondary" onClick={() => openChannel(candidate.userLogin)}><ExternalLink size={13} /> Open channel</button>
                    <button type="button" className="ffs-button primary" onClick={() => void copyRaid(candidate.userLogin)}><Copy size={13} /> Copy /raid</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="ffs-raid-safety">FFSControl only discovers channels and prepares the command. You remain responsible for reviewing the target and explicitly starting the raid in Twitch.</p>
      </section>
    </section>
  );
}
